import type { Comment as CommentType } from '@/@types/comment'
import { getNonPrimaryCommentsQueryOptions } from '@/utils/fetchers/comment'
import { css } from '@emotion/react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { flatMap, map, pick, uniqBy } from 'lodash-es'
import { AnimatePresence, motion } from 'motion/react'
import { type HTMLAttributes, useMemo } from 'react'
import Button from '../Button'
import Comment, { type CommentData } from './Comment'

export interface CommentRepliesProps extends HTMLAttributes<HTMLDivElement> {
	data: CommentType
}

export default function CommentReplies({ data, ...props }: CommentRepliesProps) {
	const {
		user,
		createdAt,
		content,
		threadId,
		targetType,
		replyToComment,
		likeCount,
		hotReplies = [],
		replyCount,
		level,
		id,
	} = data

	const {
		data: replyPages,
		fetchNextPage,
		refetch,
		hasNextPage,
		isLoading,
		isFetching,
	} = useInfiniteQuery({
		...getNonPrimaryCommentsQueryOptions({
			targetType: data.targetType,
			primaryCommentId: data.threadId,
			level: data.level,
			limit: 5,
			targetId: data.targetId,
		}),
		enabled: false,
	})
	const commentReplies = useMemo(() => {
		return map(
			uniqBy([...hotReplies, ...flatMap(replyPages?.pages, (item) => item.data)], 'id'),
			(item) => pick(item, ['id', 'targetType']) as CommentData
		)
	}, [hotReplies, replyPages?.pages])

	if (commentReplies.length === 0) {
		return null
	}

	return (
		<div
			css={css`
				margin-top: 20px;
				display: flex;
				flex-direction: column;
				gap: 20px;
			`}
			{...props}
		>
			<AnimatePresence mode="popLayout">
				{/* comment replies */}
				{map(commentReplies, (item) => (
					<motion.div
						key={item.id}
						layout
						initial={{
							opacity: 0,
						}}
						animate={{
							opacity: 1,
						}}
						exit={{
							opacity: 0,
						}}
					>
						<Comment data={item} />
					</motion.div>
				))}

				{(hasNextPage || replyCount > commentReplies.length) && (
					<motion.div
						layout
						initial={{
							opacity: 0,
						}}
						animate={{
							opacity: 1,
						}}
						exit={{
							opacity: 0,
						}}
						css={css`
							width: fit-content;
							margin: 0 auto;
						`}
					>
						<Button
							onClick={(e) => {
								e.stopPropagation()
								fetchNextPage()
							}}
							appearance="ghost"
							size="sm"
							loading={isFetching}
							disabled={isFetching}
							css={css`
								.jk-loading {
									--dot-color: var(--tint-tertiary);
								}
							`}
						>
							加载更多
						</Button>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	)
}
